// src/domain/repositories/taskRepository.js
import taskApi from '@/data/api/tasks';
import { createTaskFromObject } from '@/domain/models/task';

const local = {
  tasks: {}
  /**
   * Processes a single task object and updates the local cache.
   * @param {Object} task - The task object from the API.
   * @param {Object} localCache - The local cache to update.
   */
};

const taskRepository = {
  /**
   * Seeds the legacy mutation cache from the authoritative local read model.
   *
   * Writes still go through the remote API, but mutation guards need a current
   * task lookup after the selected read flow stops loading from that cache.
   *
   * @param {Object} tasks - Tasks keyed by id.
   * @returns {Object} The seeded cache.
   */
  replaceCache: (tasks) => {
    local.tasks = { ...tasks };
    return local.tasks;
  },

  /** @param {Object} task - Persisted task to merge into the local cache. */
  upsertCachedTask: (task) => {
    local.tasks[task.id] = task;
    return task;
  },

  /** @param {string|number} taskId - Cached task identifier to remove. */
  removeCachedTask: (taskId) => {
    delete local.tasks[taskId];
  },

  /**
   * Retrieves all tasks from the local cache or fetches from the API if the cache is empty.
   * @returns {Promise<Object>} - A promise that resolves to the tasks object.
   */
  getAllTasks: () => {
    if (Object.keys(local.tasks).length === 0) {
      // If tasks object is empty, fetch from the API
      return taskRepository.updateAllTasks().then(() => local.tasks);
    }
    return Promise.resolve(local.tasks);
  },

  /**
   * Fetches all tasks from the API and updates the local cache.
   * @returns {Promise<Object>} - A promise that resolves to the tasks object.
   */
  updateAllTasks: () => {
    return taskApi
      .list()
      .then((response) => {
        const tasks = response.data.payload;
        const localCache = {};
        Object.values(tasks).forEach((task) => {
          const newTask = createTaskFromObject(task);
          localCache[newTask.id] = newTask;
        });
        local.tasks = localCache;
        return local.tasks;
      })
      .catch((error) => {
        console.error('Failed to update tasks:', error);
        throw error;
      });
  },

  /**
   * Retrieves a task by its ID from the local cache.
   * @param {number|string} taskId - The ID of the task to retrieve.
   * @returns {Object|null} - The task object or null if not found.
   */
  getTaskById: (taskId) => {
    return local.tasks[taskId] || null;
  },

  /**
   * Creates a new task via the API and updates the local cache.
   * @param {string} taskDefinition - The definition of the task to create.
   * @returns {Promise<Object>} - A promise that resolves to the new task object.
   */
  createTask: (taskDefinition) => {
    return taskApi
      .create(taskDefinition)
      .then((response) => {
        const newTask = createTaskFromObject(response.data.payload);
        local.tasks[newTask.id] = newTask; // Add task to the local cache
        return newTask;
      })
      .catch((error) => {
        console.error('Failed to create task:', error);
        throw error;
      });
  },

  /**
   * Updates a task via the API and updates the local cache.
   * @param {number|string} taskId - The ID of the task to update.
   * @param {Object} updates - An object containing the fields to update.
   * @returns {Promise<Object>} - A promise that resolves to the updated task object.
   */
  updateTask: (taskId, updates) => {
    return taskApi
      .update(taskId, updates)
      .then((response) => {
        const updatedTask = createTaskFromObject(response.data.payload);
        local.tasks[taskId] = updatedTask; // Update the task in the local cache
        return updatedTask;
      })
      .catch((error) => {
        console.error('Failed to update task:', error);
        throw error;
      });
  },

  /**
   * Deletes a task via the API and updates the local cache.
   * @param {number|string} taskId - The ID of the task to delete.
   * @returns {Promise<void>} - A promise that resolves when the task is deleted.
   */
  deleteTask: (taskId) => {
    const task = local.tasks[taskId];
    if (!task) {
      return Promise.reject(new Error(`Task with ID ${taskId} not found`));
    }
    delete local.tasks[taskId]; // Remove task from local cache
    return taskApi.delete(taskId).catch((error) => {
      console.error('Failed to delete task:', error);
      // Restore the task in the local cache in case of error
      local.tasks[taskId] = task;
      throw error;
    });
  }
};

export default taskRepository;
